import React, { useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useInspectorRef } from '../../../hooks/useInspectorRef'
import {
  Box,
  Typography,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Grid,
} from '@mui/material'
import { FileDownload as DownloadIcon } from '@mui/icons-material'

import { apiClient } from '../../../services/api'
import { downloadBlob } from '../../../utils/download'
import { logger } from '../../../utils/logger'
import { generateInspectorTitle, useInspector } from '../../../contexts/InspectorContext'
import { HealthStatusBadge } from '../../../components/inspector/shared/HealthStatusBadge'
import { HealthCheckSection } from '../../../components/inspector/shared/HealthCheckSection'
import { SetupOverview } from '../../../components/inspector/shared/SetupOverview'
import { AttentionBanner } from '../../../components/inspector/shared/AttentionBanner'
import type { HealthCheckData, SyncCounts } from '../../../types/healthCheck'

const AREA_ORDER = [
  'daily_operations',
  'inventory_management',
  'product_management',
  'invoicing_payments_receivables',
  'accounting_financial_controls',
  'service_management',
  'access_setup',
]

const HealthCheck: React.FC = () => {
  const { fpCode: fp_code } = useInspectorRef()
  const { franchiseData } = useInspector()
  const franchiseName = franchiseData?.franchise_name || fp_code || ''
  const [data, setData] = useState<HealthCheckData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [syncCounts, setSyncCounts] = useState<SyncCounts | null>(null)
  const [syncCountsLoading, setSyncCountsLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const fetchHealthCheck = useCallback(async () => {
    if (!fp_code) {
      setError('Invalid access parameters')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get(`/inspector/health-check/${fp_code}`)
      setData(response.data)
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Invalid access token')
      } else if (err.response?.status === 404) {
        setError('Franchise partner not found or access not enabled')
      } else {
        setError('Failed to load health check data')
      }
    } finally {
      setLoading(false)
    }
  }, [fp_code])

  const fetchSyncCounts = useCallback(async () => {
    if (!fp_code) {
      setSyncCountsLoading(false)
      return
    }
    try {
      setSyncCountsLoading(true)
      const response = await apiClient.get(`/inspector/sync-counts/${fp_code}`)
      const ls = response.data?.systems?.lightspeed
      if (ls) {
        setSyncCounts({
          outlets: ls.outlets || 0,
          registers: ls.registers || 0,
          sales: ls.sales || 0,
          clients: ls.clients || 0,
          products: ls.products || 0,
        })
      }
    } catch (err: any) {
      logger.error('Error fetching sync counts for health check:', err)
    } finally {
      setSyncCountsLoading(false)
    }
  }, [fp_code])

  const refreshAll = useCallback(() => {
    fetchHealthCheck()
    fetchSyncCounts()
  }, [fetchHealthCheck, fetchSyncCounts])

  const handleExport = useCallback(async () => {
    if (!fp_code || exporting) return
    setExporting(true)
    setExportError(null)
    try {
      await downloadBlob(
        `/inspector/health-check/export/${fp_code}`,
        `health-check-${fp_code}`,
      )
    } catch (err: any) {
      logger.error('Health check export failed:', err)
      setExportError('Failed to export health check')
    } finally {
      setExporting(false)
    }
  }, [fp_code, exporting])

  useEffect(() => {
    refreshAll()
  }, [refreshAll])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={refreshAll}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    )
  }

  if (!data) return null

  const checkedDate = new Date(data.checked_at)

  // Extract usage insights data for Setup Overview
  const usageArea = data.areas.find((a) => a.key === 'usage_insights')
  const productCheck = usageArea?.checks.find((c) => c.key === 'product_structure')
  const outletCheck = usageArea?.checks.find((c) => c.key === 'outlet_profile')

  const productStructure = productCheck?.details as {
    total_active: number
    composite: number
    variant_groups: number
    variants: number
    flat: number
  } | null

  const outletProfile = outletCheck?.details as {
    total: number
    dnu_total: number
    by_type: Record<string, { count: number; dnu: number }>
  } | null

  // Extract summary breakdowns for detail cards
  const salesSummary = (usageArea?.checks.find((c) => c.key === 'sales_summary')?.details as {
    total: number; last_7d: number; last_30d: number; voided: number; on_account_open: number
  }) || null

  const clientsSummary = (usageArea?.checks.find((c) => c.key === 'clients_summary')?.details as {
    total: number; companies: number; individuals: number; with_balance: number; loyalty_enabled: number
  }) || null

  const registersSummary = (usageArea?.checks.find((c) => c.key === 'registers_summary')?.details as {
    total: number; open: number; closed: number; by_outlet: Array<{ name: string; count: number }>
  }) || null

  // Extract Xero summary from usage_insights
  const xeroSummary = (usageArea?.checks.find((c) => c.key === 'xero_summary')?.details as {
    contacts_total: number; contacts_customers: number; contacts_suppliers: number; contacts_with_overdue: number
    invoices_receivable: number; invoices_payable: number
  }) || null

  // Extract sync freshness from usage_insights (for snapshot system status)
  const syncFreshnessCheck = usageArea?.checks.find((c) => c.key === 'sync_freshness')
  const syncFreshness = (syncFreshnessCheck?.details as {
    systems: Array<{ system: string; last_completed: string | null; days_ago: number | null }>
  })?.systems || null

  // Filter out usage_insights and fully-pending areas
  const healthAreas = data.areas.filter((a) => a.key !== 'usage_insights')
  const liveAreas = healthAreas.filter(
    (a) => !a.checks.every((c) => c.status === 'pending')
  )

  // Sort live areas by defined order
  const sortedLiveAreas = [...liveAreas].sort((a, b) => {
    const aIdx = AREA_ORDER.indexOf(a.key)
    const bIdx = AREA_ORDER.indexOf(b.key)
    return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx)
  })

  // Areas with issues for the attention alert
  const issueAreas = sortedLiveAreas.filter(
    (a) => a.verdict === 'watch' || a.verdict === 'poor' || a.verdict === 'critical'
  )

  return (
    <>
      <Helmet>
        <title>{generateInspectorTitle('Health Check')}</title>
      </Helmet>

      <Box>
        {/* Page Title */}
        <Box sx={{ mb: 3 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={2}
            flexWrap="wrap"
            rowGap={1}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                <Typography variant="h4" component="h1">
                  Data Health Check
                </Typography>
                <Chip
                  label="BETA"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    height: 22,
                    bgcolor: 'warning.main',
                    color: '#fff',
                  }}
                />
              </Stack>
              {franchiseName && (
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 500, mt: 0.25, color: 'text.primary' }}
                >
                  {franchiseName}
                </Typography>
              )}
            </Box>
            <Stack alignItems="flex-end" spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    exporting ? <CircularProgress size={14} /> : <DownloadIcon />
                  }
                  onClick={handleExport}
                  disabled={exporting}
                >
              {exporting ? 'Exporting\u2026' : 'Export Excel'}
                </Button>
                <HealthStatusBadge status={data.overall_verdict} size="medium" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Generated {checkedDate.toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })} · {checkedDate.toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Typography>
            </Stack>
          </Stack>
          {exportError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setExportError(null)}>
              {exportError}
            </Alert>
          )}
        </Box>

        {/* Attention Required */}
        <AttentionBanner areas={issueAreas} overallVerdict={data.overall_verdict} />

        {/* Setup Overview */}
        <SetupOverview
          outletProfile={outletProfile || null}
          productStructure={productStructure || null}
          syncCounts={syncCounts}
          syncCountsLoading={syncCountsLoading}
          xeroSummary={xeroSummary}
          salesSummary={salesSummary}
          clientsSummary={clientsSummary}
          registersSummary={registersSummary}
          syncFreshness={syncFreshness}
        />

        {/* Health Check Areas — 2-column grid */}
        <Grid container spacing={3}>
          {sortedLiveAreas.map((area, idx) => (
            <Grid
              item
              xs={12}
              md={
                sortedLiveAreas.length % 2 === 1 && idx === sortedLiveAreas.length - 1
                  ? 12
                  : 6
              }
              key={area.key}
            >
              <HealthCheckSection
                name={area.name}
                verdict={area.verdict}
                checks={area.checks}
                fpCode={fp_code || ''}
                defaultExpanded={false}
              />
            </Grid>
          ))}
        </Grid>

      </Box>
    </>
  )
}

export default HealthCheck
